import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  DOCUMENT_LABELS,
  DeletionTarget,
  DeletionWarning,
  checkDeletion,
  softDeleteDocument,
} from "@/lib/documents/deletion";
import DocumentSyncStatus from "@/components/Documents/DocumentSyncStatus";

interface DeleteDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: DeletionTarget | null;
  onDeleted?: () => void | Promise<void>;
}

/** Bekräftelseruta för att flytta ett dokument till papperskorgen. */
export const DeleteDocumentDialog: React.FC<DeleteDocumentDialogProps> = ({
  open,
  onOpenChange,
  target,
  onDeleted,
}) => {
  const [warnings, setWarnings] = useState<DeletionWarning[]>([]);
  const [checking, setChecking] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !target) return;
    setReason("");
    setChecking(true);
    checkDeletion(target)
      .then(setWarnings)
      .catch(() => setWarnings([]))
      .finally(() => setChecking(false));
  }, [open, target]);

  const blocked = warnings.some((w) => w.blocking);

  const handleDelete = async () => {
    if (!target) return;
    if (reason.trim().length < 3) {
      toast.error("Skriv en kort orsak till borttagningen");
      return;
    }
    setSaving(true);
    try {
      await softDeleteDocument(target, reason.trim());
      toast.success("Dokumentet ligger nu i papperskorgen");
      onOpenChange(false);
      await onDeleted?.();
    } catch (e) {
      toast.error("Kunde inte ta bort dokumentet", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            Ta bort dokument
          </DialogTitle>
          <DialogDescription>
            {target && (
              <>
                <span className="font-medium text-foreground">{target.name}</span>
                {target.docNumber ? ` (Dok ${target.docNumber})` : ""} –{" "}
                {DOCUMENT_LABELS[target.type]}. Dokumentet flyttas till projektets
                papperskorg och kan återställas därifrån.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {target && (
          <DocumentSyncStatus
            target={{ projectId: target.projectId, documentId: target.id, docNumber: target.docNumber }}
            showDeletionImpact
          />
        )}

        {checking ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Kontrollerar konsekvenser…
          </p>
        ) : (
          warnings.length > 0 && (
            <ul className="space-y-2 rounded-lg border border-border bg-muted/40 p-3">
              {warnings.map((w, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <AlertTriangle
                    className={`mt-0.5 h-4 w-4 shrink-0 ${
                      w.blocking ? "text-destructive" : "text-muted-foreground"
                    }`}
                  />
                  <span className={w.blocking ? "text-destructive" : "text-muted-foreground"}>
                    {w.message}
                  </span>
                </li>
              ))}
            </ul>
          )
        )}

        {!blocked && (
          <div className="space-y-2">
            <Label htmlFor="delete-reason">Orsak till borttagningen</Label>
            <Textarea
              id="delete-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="T.ex. dubblett, skapad av misstag, ersatt av nyare version"
              rows={3}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={blocked || saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Flytta till papperskorgen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteDocumentDialog;
